/**
 * @author Rosa
 */
import { GetUsernameResponse, SaveUsernameResponse, UsernameService } from "../services/UsernameService";

/**
 * Custom element to handle username input and validation.
 * @class UsernameComponent
 * @extends HTMLElement
 */
export class UsernameComponent extends HTMLElement {
    private _usernameService: UsernameService = new UsernameService();
    private input!: HTMLInputElement;
    private button!: HTMLButtonElement;
    private errorMessage!: HTMLDivElement;
    private overlay!: HTMLDivElement;

    /**
     * connectedCallback
     * This method is called when the element is inserted into the DOM.
     * It sets up the shadow DOM, renders the component, and fetches the username.
     * @async
     * @returns {Promise<void>}
     */
    public async connectedCallback(): Promise<void> {
        this.attachShadow({ mode: "open" });
        this.render();

        try {
            const response: GetUsernameResponse = await this._usernameService.getUsername();
            if (response.username) {
                this.overlay.style.display = "none";
                window.dispatchEvent(new CustomEvent("username:entered"));
            }
        }
        catch (reason) {
            this.showError("Unable to connect. Please check your internet connection.");
            console.error("Error with username:", reason);
        }
    }

    /**
     * render
     * renders the HTML structure and styles for the component
     * Eventlisteners for user interacactions.
     * @returns {void}
     */
    public render(): void {
        if (!this.shadowRoot) {
            return;
        }
        this.shadowRoot.innerHTML = `
            <style>
            .overlay {
                position: fixed;
                top: 0;
                left: 0;
                width: 100vw;
                height: 100vh;
                background: rgba(0, 0, 0, 0.85);
                display: flex;
                justify-content: center;
                align-items: center;
                z-index: 9999;
                backdrop-filter: blur(4px);
                box-shadow: inset 0 0 200px rgba(12, 213, 231, 0.1);
                animation: fadeIn 0.3s ease-in-out;
            }

            .popup-content {
                background-color: rgba(20, 20, 59, 0.95);
                padding: 40px;
                border: 2px solid rgb(178, 103, 245);
                border-radius: 16px;
                box-shadow: 0 0 10px rgb(169, 2, 230);
                display: flex;
                flex-direction: column;
                align-items: center;
                animation: popupZoom 0.4s ease-out;
                width: 360px;
                height: 470px; 
            }

            .popup-content h2 {
                font-size: 2rem;
                font-weight: bold;
                color: rgb(180, 118, 238);
                text-shadow: none !important;
                margin-bottom: 5px;
                margin-top: -10px;
                text-align: center;
                white-space: nowrap;
            }

            .popup-content hr {
                width: 100%;
                border: 1px solid rgb(178, 103, 245);
                margin: 10px 0;
            }

            .popup-content p {
                color: rgb(167, 206, 231) !important;
                margin-bottom: 5px;
                text-align: center;
                text-shadow: none !important;
            }

            .popup-content input {
                padding: 10px;
                margin-top: 30px;
                border-radius: 5px;
                border: 3px solid rgb(161, 103, 211);
                background: transparent;
                color: rgb(246, 244, 249);
                width: 320px;
            }

            .button-container {
                display: flex;
                justify-content: center;
                gap: 5%;
                width: 100%;
                margin-top: 20px;
            }

            .popup-content button {
                padding: 10px 20px;
                margin-top: 20px;
                border-radius: 5px;
                border: 1px solid rgb(121, 73, 167);
                background: rgb(127, 81, 187);
                color: black;
                cursor: pointer;
                transition: 0.3s;
            }

            .popup-content button:hover {
                background: rgb(188, 138, 253);
                transform: scale(1.10); 
            }

            .popup-content #return-button {
                padding: 10px 20px;
                margin-top: 20px;
                border-radius: 5px;
                border: 1px solid rgb(121, 73, 167);
                background: rgb(187, 71, 71);
                color: black;
                cursor: pointer;
                transition: 0.3s;
            }

            .popup-content #return-button:hover {
                background: rgb(248, 103, 103)
            }

            .popup-content #error-message {
                color: rgb(206, 69, 87);
                font-size: 1.2rem;
                margin-top: 10px;
                text-align: center;
                text-shadow: none;
            }

            @keyframes popupZoom {
                0% {
                    opacity: 0;
                    transform: scale(0.8);
                }
                100% {
                    opacity: 1;
                    transform: scale(1);
                }
            }

            @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
            }

            @media (max-width: 600px) {
                .popup-content {
                    width: 90%;
                    padding: 20px;
                }

                .popup-content h2 {
                    font-size: 1.5rem;
                }

                .popup-content input {
                    width: 100%;
                }

                .popup-content p {
                    font-size: 0.9rem;
                }

                .button-container {
                flex-direction: column;
                align-items: center;
            }

            .button-container button {
                width: 100%;
                margin-top: 10px;
            }
        }
        </style>
        <div class="overlay">
            <div class="popup-content">
                <h2>Enter your username</h2>
                <hr>
                <p>Please enter your username to start the game. A good username is between 3 and 30 characters long!</p>
                <p style="margin-bottom: 2px;">Choose wisely, you won't be able to change your chosen username afterwards.</p>
                <input type="text" id="username-input" placeholder="Username" />
                <div class="button-container">
                    <button id="return-button">Return</button>
                    <button id="submit-button">Submit</button>
                </div>
                <div id="error-message"></div>
            </div>
        </div>
    `;

        this.input = this.shadowRoot.querySelector("#username-input") as HTMLInputElement;
        this.button = this.shadowRoot.querySelector("#submit-button") as HTMLButtonElement;
        const returnButton: HTMLButtonElement = this.shadowRoot.querySelector("#return-button") as HTMLButtonElement;
        this.errorMessage = this.shadowRoot.querySelector("#error-message") as HTMLDivElement;
        this.overlay = this.shadowRoot.querySelector(".overlay") as HTMLDivElement;

        this.button.removeEventListener("click", this.submitUsername.bind(this));
        this.button.addEventListener("click", this.submitUsername.bind(this));
        returnButton.removeEventListener("click", this.returnToPrevious.bind(this));
        returnButton.addEventListener("click", this.returnToPrevious.bind(this));
        this.input.removeEventListener("keydown", this.handleEnterKey.bind(this));
        this.input.addEventListener("keydown", this.handleEnterKey.bind(this));
    }

    /**
     * submitUsername
     * This method is called when the user clicks the submit button.
     * It validates the username and sends it to the server.
     * @async
     * @returns {Promise<void>}
     */
    private async submitUsername(): Promise<void> {
        const username: string = this.input.value.trim();

        if (username.length === 0) {
            this.showError("Username cannot be empty.");
            return;
        }
        if (username.length < 3) {
            this.showError("Username cannot be shorter than 3 characters.");
            return;
        }
        if (username.length > 30) {
            this.showError("Username cannot be longer than 30 characters.");
            return;
        }
        if (!/^[a-zA-Z0-9_]+$/.test(username)) {
            this.showError("Username can only contain letters, numbers, and underscores.");
            return;
        }

        try {
            const response: SaveUsernameResponse = await this._usernameService.saveUsername(username);

            if (!response.available || typeof response.available !== "boolean" || typeof response.message !== "string") {
                this.showError(response.message);
                return;
            }

            this.overlay.style.display = "none";
            window.dispatchEvent(new CustomEvent("username:entered"));
            window.location.reload();
        }
        catch (reason) {
            this.showError("An error occurred. Please try again later.");
            console.log("something has happened", reason);
        }
    }

    /**
     * returnToPrevious
     * This method is called when the user clicks the return button.
     * It hides the overlay and dispatches a custom event.
     * @returns {void}
     */
    private returnToPrevious(): void {
        this.overlay.style.display = "none";
        window.dispatchEvent(new CustomEvent("username:skipped"));
    }

    /**
     * handleEnterKey
     * This method is called when the user presses a key in the input field.
     * If the Enter key is pressed, it submits the username.
     * @param {KeyboardEvent} event - The keyboard event.
     * @returns {void}
     */
    private handleEnterKey(event: KeyboardEvent): void {
        if (event.key === "Enter") {
            void this.submitUsername();
        }
    }

    /**
     * showError
     * This method displays an error message in the div.
     * @param {string} message - The error message to display.
     * @returns {void}
     */
    private showError(message: string): void {
        this.errorMessage.textContent = message;
    }
};
